// Copyright (c) 2026, Dokos SAS and Contributors
// License: GNU General Public License v3. See license.txt
// @dokos: Plan mode of the Project Management view on a narrow screen. Same `get_plan`
// payload, cells and dialogs as the timeline (PMViewCalendar_Plan), laid out for a phone:
// a day strip (team load per day) and one card per project with, for the selected day,
// the work of each employee, then the tasks and the quick "add a task" input. Taps
// replace the timeline's select / drag: assign buttons, chips opening their assignment.
// Plain DOM (no FullCalendar), with a `fullcalendar` shim for the view and the helpers.

import { PMViewCalendar_Plan, TEAM_EXPANDED_KEY } from "./PMViewCalendar_Plan";
import {
	activityColor,
	daysBetween,
	formatDay,
	formatHours,
	formatMinutes,
	h,
	remainingText,
	renderChip,
	scrollIntoStrip,
	ymd,
} from "./PMRender";

/** Displayed period of each range (the timeline's pmWeek / pm2Weeks / pmMonth views). */
const RANGES = { week: { weeks: 1 }, "2weeks": { weeks: 2 }, month: { months: 1 } };

const today = () => frappe.datetime.get_today();
const isWeekend = (day) => moment(day, "YYYY-MM-DD").isoWeekday() >= 6;
const datePart = (value) => (value ? String(value).slice(0, 10) : null);

export class PMViewMobile_Plan extends PMViewCalendar_Plan {
	constructor(opts) {
		super(opts);
		this.range = "week";
		this.anchor = this.view?._date || today();
		this.selectedDay = null;
		this.loadId = 0;

		const calendar = this;
		// the view reads/moves the displayed date, the Plan helpers read the period
		this.fullcalendar = {
			getDate: () => moment(calendar.anchor, "YYYY-MM-DD").toDate(),
			gotoDate: (date) => calendar.goto(ymd(date)),
			refetchResources: () => calendar.render(), // a project or the team was toggled
			refetchEvents: () => {},
			unselect: () => {},
			get view() {
				const { start, end } = calendar.period();
				return { activeStart: start, activeEnd: end };
			},
		};
	}

	// -------------------------------------------------------------------------------
	// Contract of the view
	// -------------------------------------------------------------------------------

	async make() {
		this.page.clear_user_actions();
		this.root = h("div", { class: "pm-plan-m" });
		this.parent.appendChild(this.root);
		this.render();
		await this.load();
	}

	destroy() {
		this.disposePickers();
		this.root?.remove();
		this.root = null;
	}

	refresh() {
		this._cache = { key: null, promise: null };
		return this.load();
	}

	// -------------------------------------------------------------------------------
	// Period
	// -------------------------------------------------------------------------------

	/** `{start, end}` ISO days, `end` excluded (as FullCalendar's active range). */
	period() {
		const anchor = moment(this.anchor, "YYYY-MM-DD");
		let start;
		if (this.range === "month") {
			start = anchor.clone().startOf("month");
		} else {
			const firstDay = frappe.datetime.get_first_day_of_the_week_index();
			start = anchor.clone().subtract((anchor.day() - firstDay + 7) % 7, "days");
		}
		const end = start.clone().add(RANGES[this.range]);
		return { start: start.format("YYYY-MM-DD"), end: end.format("YYYY-MM-DD") };
	}

	days() {
		const { start, end } = this.period();
		return daysBetween(start, moment(end, "YYYY-MM-DD").subtract(1, "day").format("YYYY-MM-DD"));
	}

	/** The selected day, else today when displayed, else the first day. */
	currentDay() {
		const days = this.days();
		if (days.includes(this.selectedDay)) return this.selectedDay;
		return days.includes(today()) ? today() : days[0];
	}

	goto(day) {
		this.anchor = day;
		this.selectedDay = day;
		this.view?.update_url_with_filters?.();
		this.render();
		return this.load();
	}

	shift(delta) {
		const { start } = this.period();
		const unit = Object.entries(RANGES[this.range])[0];
		this.anchor = moment(start, "YYYY-MM-DD")
			.add(unit[1] * delta, unit[0])
			.format("YYYY-MM-DD");
		this.selectedDay = null;
		this.view?.update_url_with_filters?.();
		this.render();
		return this.load();
	}

	setRange(range) {
		if (!RANGES[range] || range === this.range) return;
		this.range = range;
		this.anchor = this.currentDay(); // the new period holds the selected day
		this.render();
		return this.load();
	}

	selectDay(day) {
		this.selectedDay = day;
		this.render();
	}

	async load() {
		const id = ++this.loadId;
		const { start, end } = this.period();
		this.root?.classList.add("is-loading");
		try {
			const payload = await this.getPlan({ start, end });
			if (id !== this.loadId || !this.root) return;
			this.payload = payload;
		} finally {
			if (id === this.loadId) this.root?.classList.remove("is-loading");
		}
		this.render();
	}

	// -------------------------------------------------------------------------------
	// Rendering
	// -------------------------------------------------------------------------------

	render() {
		if (!this.root) return;
		// the rows are rebuilt: give the focus back to the same control (data-pm-key)
		const focusKey = this.root.contains(document.activeElement)
			? document.activeElement.closest("[data-pm-key]")?.dataset.pmKey
			: null;
		this.disposePickers();
		const strip = this.renderDayStrip();
		this.root.replaceChildren(this.renderToolbar(), strip, this.renderBody());
		scrollIntoStrip(strip, strip.querySelector('[aria-pressed="true"]'));
		if (focusKey) {
			const target = this.root.querySelector(`[data-pm-key="${CSS.escape(focusKey)}"]`);
			// @dokos: a tab-buttons group is not focusable itself: focus its selected pill
			(target?.matches(".es-tab-buttons")
				? target.querySelector('[aria-checked="true"]')
				: target
			)?.focus();
		}
	}

	renderToolbar() {
		const days = this.days();
		// @dokos: es-buttons (façade), icon-only and named by their title
		const iconButton = (label, name, key, onClick) =>
			erpnext.dokos.ui.button({
				icon: name,
				title: label,
				attrs: { "data-pm-key": key },
				onclick: onClick,
			})[0];
		// @dokos: the period switch is the same es-tab-buttons as the timeline's (was a <select>)
		const range = erpnext.dokos.ui.tab_buttons({
			label: __("Period"),
			value: this.range,
			css_class: "pm-period-switch",
			options: [
				{ value: "week", label: __("Week") },
				{ value: "2weeks", label: __("2 weeks", null, "PM View") },
				{ value: "month", label: __("Month") },
			],
			on_change: (value) => this.setRange(value),
		})[0];
		range.dataset.pmKey = "range";
		return h(
			"div",
			{ class: "pm-plan-m-toolbar" },
			iconButton(__("Previous"), "chevron-left", "prev", () => this.shift(-1)),
			iconButton(__("Next"), "chevron-right", "next", () => this.shift(1)),
			h("h3", {
				class: "pm-plan-m-title ellipsis",
				text: `${formatDay(days[0], "dayMonth")} – ${formatDay(days.at(-1), "dayMonth")}`,
				attrs: { "aria-live": "polite" },
			}),
			iconButton(__("Today"), "calendar-check", "today", () => this.goto(today())),
			range
		);
	}

	renderDayStrip() {
		const current = this.currentDay();
		const loads = this.payload ? this.teamLoad(this.payload) : {};
		const strip = h("div", {
			class: "pm-plan-m-days",
			attrs: { role: "group", "aria-label": __("Day", null, "PM View") },
		});
		for (const day of this.days()) {
			const { load = 0, capacity = 0 } = loads[day] || {};
			const summary = load || capacity ? `${formatMinutes(load)} / ${formatMinutes(capacity)}` : "";
			strip.append(
				h(
					"button",
					{
						type: "button",
						class: [
							"pm-plan-m-day",
							isWeekend(day) && "is-weekend",
							day === today() && "is-today",
						],
						attrs: {
							"aria-pressed": day === current ? "true" : "false",
							"aria-label": [formatDay(day, "longDay"), summary].filter(Boolean).join(", "),
							"data-pm-key": `day::${day}`,
						},
						on: { click: () => this.selectDay(day) },
					},
					h("span", { class: "pm-plan-m-day-label", text: formatDay(day, "weekdayDay") }),
					load || capacity
						? this.renderRatio(load, capacity, "pm-plan-m-ratio")
						: h("span", { class: "pm-plan-m-ratio pm-plan-m-none", text: "—" })
				)
			);
		}
		return strip;
	}

	renderBody() {
		const body = h("div", { class: "pm-plan-m-body" });
		if (!this.payload) {
			// @dokos: skeleton while the plan loads
			body.append(
				h(
					"div",
					{ class: "pm-plan-m-empty", attrs: { "aria-busy": "true" } },
					erpnext.dokos.ui.skeleton({ width: "100%", height: "120px" })[0],
					h("span", { class: "sr-only", text: __("Loading...") })
				)
			);
			return body;
		}
		const day = this.currentDay();
		const { load = 0, capacity = 0 } = this.teamLoad(this.payload)[day] || {};
		body.append(
			h(
				"div",
				{ class: "pm-plan-m-summary" },
				h("h4", { class: "pm-plan-m-summary-day", text: formatDay(day, "longDay") }),
				h(
					"div",
					{ class: "pm-plan-m-summary-load" },
					h("span", { class: "dk-muted", text: __("Team load", null, "PM View") }),
					this.renderTeamRatio(load, capacity)
				)
			)
		);
		// @dokos: the timeline's rows (Project Type groups, Tasks | Team, task tree) as cards
		let card = null;
		for (const row of this.planRows(this.payload)) {
			if (row.props.kind === "group") {
				body.append(this.renderGroupHeader(row.props));
				card = null;
			} else if (row.props.kind === "project") {
				card = { props: row.props };
				body.append(this.renderProjectCard(card));
			} else if (card) {
				card.el.querySelector(".pm-plan-m-rows").append(this.renderPlanRow(row.props, day));
			}
		}
		if (!this.payload.projects.length) {
			// @dokos: façade empty state
			body.append(
				erpnext.dokos.ui.empty_state({
					icon: "folder-open",
					title: __("No {0} found", [__("Project")]),
					css_class: "pm-plan-m-empty",
				})[0]
			);
		}
		body.append(this.renderTeamCard(day));
		return body;
	}

	/** @dokos: a Project Type separator between the cards. */
	renderGroupHeader(props) {
		return h("div", { class: "pm-plan-m-group" }, this.renderGroupCell(props));
	}

	/** A project card; its rows are appended to `.pm-plan-m-rows` by `renderBody`. */
	renderProjectCard(card) {
		const { project, expanded } = card.props;
		const cell = this.renderProjectCell(card.props);
		cell.querySelector(".pm-toggle")?.setAttribute("data-pm-key", `toggle::${project.name}`);
		card.el = h(
			"section",
			{
				class: ["pm-plan-m-card", expanded && "is-expanded"],
				attrs: { "aria-label": project.project_name || project.name },
			},
			h(
				"div",
				{ class: "pm-plan-m-card-head" },
				cell,
				this.renderPlannedCell({ extendedProps: { kind: "project", project } })
			),
			expanded ? h("div", { class: "pm-plan-m-rows" }) : null
		);
		return card.el;
	}

	/** @dokos: one row of a project card (see `planRows`). */
	renderPlanRow(props, day) {
		switch (props.kind) {
			case "employee":
			case "taskemployee":
				return this.renderEmployeeRow(props, day);
			case "task":
				return this.renderTaskRow(props);
			case "add":
				return this.renderAddCell(props);
			default:
				return h("div");
		}
	}

	/** An employee of the project: load / capacity of the day (all projects), the day's chips. */
	renderEmployeeRow(props, day) {
		const { project, task, employee: emp, chips, activityType } = props;
		const name = emp.employee_name || emp.employee;
		const load = this.payload.load?.[emp.employee]?.[day] || 0;
		const capacity = this.payload.capacity?.[emp.employee]?.[day] || 0;

		const work = h("div", { class: "pm-plan-m-work" });
		for (const ev of chips.filter((chip) => chip.date === day)) {
			const over = this.isOver(ev.employee, day);
			const label = [
				formatMinutes(ev.minutes),
				ev.activity_type && __(ev.activity_type),
				over ? __("over capacity", null, "PM View") : null,
			]
				.filter(Boolean)
				.join(" · ");
			work.append(
				h(
					"button",
					{
						type: "button",
						class: ["pm-plan-m-chip", over && "pm-over"],
						title: label,
						style: {
							"--pm-color": activityColor(ev.activity_type, ev.activity_color, ev.project),
						},
						attrs: { "aria-label": `${name}, ${label}`, "aria-haspopup": "dialog" },
						// @dokos: the same actions as a timeline chip (edit, remove the day, replace, delete)
						on: { click: (e) => this.openChipPopover(e.currentTarget, { data: ev, over }) },
					},
					renderChip({ minutes: ev.minutes, activityType: ev.activity_type, over })
				)
			);
		}
		const assign = this.assignButton(
			__("Assign {0} on {1}", [name, formatDay(day, "longDay")], "PM View"),
			() =>
				this.openAssignment({
					project: project.name,
					task: task?.name, // @dokos: an employee of a task Team row
					employees: [emp.employee],
					activity_type: activityType || undefined,
					start_date: day,
					end_date: day,
				})
		);
		if (assign) work.append(assign); // null without the create permission
		return h(
			"div",
			{ class: "pm-plan-m-row", style: props.depth ? { "--pm-depth": props.depth } : null },
			h(
				"div",
				{ class: "pm-plan-m-row-head" },
				this.renderEmployeeCell(props),
				load || capacity ? this.renderRatio(load, capacity, "pm-plan-m-ratio", true) : null
			),
			work
		);
	}

	/** A task of the project, with its expected dates and hours under the timeline cell. */
	renderTaskRow(props) {
		const { task, assignees, depth } = props;
		const start = datePart(task.exp_start_date) || datePart(task.exp_end_date);
		const end = datePart(task.exp_end_date) || start;
		const dates = start
			? end && end > start
				? `${formatDay(start, "dayMonth")} → ${formatDay(end, "dayMonth")}`
				: formatDay(start, "dayMonth")
			: null;
		const meta = [
			dates,
			task.expected_time ? formatHours(task.expected_time) : null,
			assignees.length ? null : __("Unassigned", null, "PM View"),
		].filter(Boolean);
		return h(
			"div",
			{
				class: ["pm-plan-m-row", "pm-plan-m-row--task", !assignees.length && "is-unassigned"],
				style: { "--pm-depth": depth },
			},
			this.renderTaskCell(props),
			meta.length ? h("div", { class: "pm-plan-m-meta", text: meta.join(" · ") }) : null
		);
	}

	/** @dokos: "32h / 40h" + a bar, then what is left of the capacity ("Remaining 8h"). */
	renderTeamRatio(load, capacity) {
		const ratio = this.renderRatio(load, capacity, "pm-plan-m-ratio", true);
		const { level, text } = remainingText(load, capacity);
		ratio.append(h("span", { class: ["pm-plan-m-remaining", `pm-level--${level}`], text }));
		return ratio;
	}

	/** Team load: each employee's load / capacity of the day across all projects. */
	renderTeamCard(day) {
		const expanded = this.isExpanded(TEAM_EXPANDED_KEY, -1);
		const cell = this.renderLoadCell({ expanded });
		cell.querySelector(".pm-toggle")?.setAttribute("data-pm-key", "toggle::team");
		const card = h(
			"section",
			{
				class: ["pm-plan-m-card", "pm-plan-m-card--team", expanded && "is-expanded"],
				attrs: { "aria-label": __("Team load", null, "PM View") },
			},
			h("div", { class: "pm-plan-m-card-head" }, cell)
		);
		if (!expanded) return card;

		const extra = new Set(this.getExtraEmployees());
		const rows = h("div", { class: "pm-plan-m-rows" });
		for (const emp of this.payload.team || []) {
			const load = this.payload.load?.[emp.employee]?.[day] || 0;
			const capacity = this.payload.capacity?.[emp.employee]?.[day] || 0;
			rows.append(
				h(
					"div",
					{ class: "pm-plan-m-row" },
					h(
						"div",
						{ class: "pm-plan-m-row-head" },
						this.renderTeamCell({ employee: emp, extra: extra.has(emp.employee) }),
						load || capacity ? this.renderTeamRatio(load, capacity) : null
					)
				)
			);
		}
		rows.append(this.renderTeamAddCell());
		card.append(rows);
		return card;
	}
}
